import { Injectable, Output, EventEmitter } from '@angular/core';
import { TestParameter, Parameter } from '../../../../shared/models/specification/test-parameter.model';
import { ParameterSpecification } from '../../../../shared/models/specification/prarameter-specification.model';
import { SpecificationViewModel } from '../../../../shared/models/specification/specification-view-model.model';
import { Unit } from '../../../../shared/models/specification/units.model';
import { Dictionary, TranslationService } from 'projects/dop-core/src/public-api';
import { Specification } from 'src/app/shared/models/specification/specification.model';
import { Sample } from 'src/app/shared/models/sample.model';
import { ResultConversionCode } from 'src/app/shared/models/specification/result-conversion-code.model';
import { ISubColumn, ISpecificationHeader } from 'src/app/shared/models/specification/specification-headers.model';
import { LinkedSpecification } from 'src/app/shared/models/linked-specification.model';
import { SpecificationType } from 'src/app/shared/models/specification/specification-type.model';
import { AppConfigService } from 'src/app/core/app-config/app-config.service';
import { ParameterType } from 'src/app/shared/models/specification/parameter-type.model';

@Injectable({
  providedIn: 'root'
})
export class SpecificationHelper {
  @Output() specificationUpdater = new EventEmitter();

  constructor(private appConfigService: AppConfigService) { }

  currentSample: Sample;
  units: Unit[];

  convertToSpecificationViewModel(
    sample: Sample,
    units: Unit[],
    linkedSpecifications: LinkedSpecification[] = null, isPending: boolean = false): SpecificationViewModel[] {
    this.currentSample = sample;
    this.units = units;
    let testParameters: TestParameter[] = sample?.testParameters ?? [];
    const sampleDetails: SpecificationViewModel[] = sample?.specifications;
    testParameters = testParameters.filter(p => p.parameters);
    if (testParameters && testParameters.length > 0) {
      testParameters = testParameters.sort((a, b) => (a.testName > b.testName) ? 1 : -1);
    }
    let parameterSpecifications = this.convertToParameterSpecifications(testParameters, units, linkedSpecifications, isPending);

    const requiredParameters = parameterSpecifications.filter(t => t.required)
      .sort((a, b) => a.testName > b.testName ? 1 : -1);
    const nonRequiredParameters = parameterSpecifications.filter(t => !t.required)
      .sort((a, b) => a.testName > b.testName ? 1 : -1);

    parameterSpecifications = requiredParameters.concat(nonRequiredParameters);

    if (sampleDetails) {
      const existingParameterSpecifications = ([] as ParameterSpecification[])
        .concat(...sampleDetails.map((t: SpecificationViewModel) => t.parameters));
      existingParameterSpecifications.forEach(t => {
        parameterSpecifications.forEach(l => {
          if (t.parameterCode === l.parameterCode) {
            this.updateExistingSpecifications(l, t);
          }
        });
      });
    }

    return this.getSpecifications(parameterSpecifications);
  }

  convertToParameterSpecifications(testParameters: TestParameter[], units: Unit[], linkedSpecifications: LinkedSpecification[] = null, isPending: boolean = false)
    : ParameterSpecification[] {
    const dict = new Dictionary<ParameterSpecification>();

    if (testParameters.length === 0) {
      return [] as ParameterSpecification[];
    }

    testParameters.forEach(t => {

      t.parameters.forEach(p => {
        if (dict.containsKey(p.code)) {
          const parameter = dict.get(p.code);
          if (parameter.analyses.findIndex(a => a.code === p.testCode) < 0) {
            parameter.analyses.push({ code: p.testCode, name: t.testName });
            parameter.testName = parameter.testName + '/' + t.testName;
            const pName = (p.localisedName && p.localisedName.trim()) ? p.localisedName : p.name;
            if (parameter.parameterName !== pName && !parameter.parameterName.split('/').includes(pName)) {
              parameter.parameterName = parameter.parameterName + '/' + pName;
            }
            parameter.required = parameter.required || p.requiresExpectedValue;
            dict.update(p.code, parameter);
          }
        } else {
          const obj: ParameterSpecification = {
            analyses: [{ code: p.testCode, name: t.testName }],
            parameterName: (p.localisedName && p.localisedName.trim()) ? p.localisedName : p.name,
            testCode: t.testCode,
            testName: t.testName,
            parameterCode: p.code,
            required: p.requiresExpectedValue,
            parameterType: p.parameterType,
            listValues: p.listValues,
          };
          this.initializeSpecifications(p, obj, units, linkedSpecifications, isPending);
          dict.add(p.code, obj);
        }
      });
    });
    return dict.values().sort((a, b) => (a.testName > b.testName ? 1 : -1));
  }

  private getSpecifications(
    parameters: ParameterSpecification[]): SpecificationViewModel[] {
    const specifications: SpecificationViewModel[] = [];
    for (let i = 0; i < parameters.length; i++) {
      const rowData = parameters[i];
      const testName = rowData.testName;
      if (i === 0) {
        this.insertSpecification(rowData, specifications);
        this.AddparametertoSpecification(specifications, testName, rowData);
      }      
      else{
        const previousRowData = parameters[i - 1];
        if (JSON.stringify(rowData.analyses) === JSON.stringify(previousRowData.analyses)
          && rowData.required === previousRowData.required) {
            rowData.isSelectAll = false;
            this.AddparametertoSpecification(specifications, testName, rowData);
        } else {
          this.insertSpecification(rowData, specifications);
          this.AddparametertoSpecification(specifications, testName, rowData);
        }
      }
    }
    return specifications;
  }
 
  private AddparametertoSpecification(specifications: SpecificationViewModel[], testName: string, rowData: ParameterSpecification){
    if (specifications.findIndex(t => t.testName === testName) >= 0) {
      const index = specifications.map(x => x.testName === testName).lastIndexOf(true);
      rowData.isSelectAll = false;
      specifications[index].parameters.push(rowData);
      specifications[index].parameters.sort((a: any, b: any) => (a.parameterName > b.parameterName) ? 1 : -1);
      specifications[index].parametersViewModels.push(rowData);
      specifications[index].parametersViewModels.sort((a: any, b: any) => {
        // If a parameterName is null or undefined, it should come first
        if (a?.parameterName == null && b?.parameterName != null) return -1; // a comes first
        if (b?.parameterName == null && a?.parameterName != null) return 1; // b comes first
      
        // If both are not null/undefined, compare alphabetically
        return (a?.parameterName > b?.parameterName) ? 1 : -1;
      });
    }
  }

  // private insertSpecification(rowData: ParameterSpecification, specifications: SpecificationViewModel[]) {
  //   specifications.push({
  //     testName: rowData.testName,
  //     testCode: rowData.testCode,
  //     selectAll: true
  //   });

  private insertSpecification(rowData: ParameterSpecification, specifications: SpecificationViewModel[]) {
    var parametersViewModel = new ParameterSpecification();
    parametersViewModel.isSelectAll = true;
    parametersViewModel.standardSpecification = rowData.standardSpecification;
    parametersViewModel.servingSpecification = rowData.servingSpecification;
    parametersViewModel.dailyValueSpecification = rowData.dailyValueSpecification;
    parametersViewModel.labelClaimSpecification = rowData.labelClaimSpecification;
    parametersViewModel.parameterType = ParameterType.None;
    rowData.isSelectAll = true;
    specifications.push({
      testName: rowData.testName,
      testCode: rowData.testCode,
      parameters: new Array(),
      parametersViewModels: [parametersViewModel],
    });
  }

  isSpecificationHasValues(specification: Specification, isServingSpecification: boolean = false): boolean {
    return (this.hasDenominatorOrDenominatorChanged(specification, isServingSpecification) ||
      specification.target || specification.selectedLOV
      || specification.max || specification.min) ? true : false;
  }

  private hasDenominatorOrDenominatorChanged(specification: Specification, isServingSpecification: boolean): boolean {
    return (!isServingSpecification && (specification.denominatorCode && specification.defaultDenominator &&
      specification.defaultDenominator.code !== specification.denominatorCode.code)) ||
      (specification.numeratorCode && specification.defaultNumerator &&
        specification.defaultNumerator.code !== specification.numeratorCode.code);
  }

  updateExistingSpecifications(
    newParameterSpecification: ParameterSpecification,
    existingParameterSpecification: ParameterSpecification) {
    this.updateSpecification(newParameterSpecification.standardSpecification, existingParameterSpecification.standardSpecification, false, newParameterSpecification.listValues);
    if (existingParameterSpecification.servingSpecification) {
      this.updateSpecification(newParameterSpecification.servingSpecification,
        existingParameterSpecification.servingSpecification, true, newParameterSpecification.listValues);
    }
    if (existingParameterSpecification.dailyValueSpecification) {
      this.updateSpecification(newParameterSpecification.dailyValueSpecification,
        existingParameterSpecification.dailyValueSpecification, false, newParameterSpecification.listValues);
    }
    if (existingParameterSpecification.labelClaimSpecification) {
      this.updateSpecification(newParameterSpecification.labelClaimSpecification,
        existingParameterSpecification.labelClaimSpecification, false, newParameterSpecification.listValues);
    }
  }

  updateSpecification(newSpecification: Specification, existingSpecification: Specification, isServing = false, listValues: any[] = null) {
    newSpecification.target = existingSpecification.target;
    newSpecification.min = existingSpecification.min;
    newSpecification.max = existingSpecification.max;

    newSpecification.numeratorCode = this.units
      .filter((unit: Unit) => unit.code === existingSpecification.numeratorCode?.code)?.shift();

    if (!isServing) {
      newSpecification.denominatorCode = this.units
        .filter((unit: Unit) => unit.code === existingSpecification.denominatorCode?.code)?.shift();
    }

    // Restore selectedLOV by finding matching item from listValues
    if (existingSpecification.selectedLOV && listValues) {
      newSpecification.selectedLOV = listValues.filter(x => x.code === existingSpecification.selectedLOV?.code)?.shift();
    }

    newSpecification.hasValue = this.isSpecificationHasValues(existingSpecification);
  }

  initializeSpecifications(
    parameter: Parameter, parameterSpecification: ParameterSpecification, units: Unit[],
    linkedSpecifications: LinkedSpecification[], isPending: boolean = false) {
    parameterSpecification.standardSpecification
      = this.initializeSpecification(parameter, units, linkedSpecifications && linkedSpecifications
        .filter(x => x.specificationType.toLowerCase() === SpecificationType[SpecificationType.Standard].toLowerCase()), true);
    if (this.currentSample?.resultConversion === ResultConversionCode.DV) {
      parameterSpecification.dailyValueSpecification
        = this.initializeSpecification(parameter, units, linkedSpecifications && linkedSpecifications
          .filter(x => x.specificationType.toLowerCase() === SpecificationType[SpecificationType.DailyValue].toLowerCase()));
    } else if (this.currentSample?.resultConversion === ResultConversionCode.LC) {
      parameterSpecification.labelClaimSpecification
        = this.initializeSpecification(parameter, units, linkedSpecifications && linkedSpecifications
          .filter(x => x.specificationType.toLowerCase() === SpecificationType[SpecificationType.LabelClaim].toLowerCase()));
    }
    if (this.currentSample?.servingSize && Number(this.currentSample?.servingSize)) {
      parameterSpecification.servingSpecification
        = this.initializeSpecification(parameter, units, linkedSpecifications
          && linkedSpecifications.filter(x => x.specificationType.toLowerCase()
            === SpecificationType[SpecificationType.ServingPortion].toLowerCase()), false, true, isPending);
    }
  }

  initializeSpecification(
    parameter: Parameter, units: Unit[], linkedSpecifications: LinkedSpecification[],
    isStandard: boolean = false, isServing: boolean = false, isPending: boolean = false): Specification {
    const existingSpecification = linkedSpecifications?.filter(x => x.parameterCode === parameter.code)?.shift();

    const specification: Specification = {
      target: existingSpecification?.expectedValue ?? '',
      selectedLOV: parameter.listValues?.filter(x => x.code === parameter.selectedLOV?.code).shift(),
      isUpdated: false,
      hasValue: false,
      min: existingSpecification?.lowerLimit ?? '',
      max: existingSpecification?.upperLimit ?? ''
    };
    if ((isStandard || isServing) && parameter.numerator?.code) {
      specification.defaultNumerator = {
        code: parameter.numerator.code,
        name: parameter.numerator.name,
      };
    }
    if (isStandard && parameter.denominator?.code) {
      specification.defaultDenominator = {
        code: parameter.denominator.code,
        name: parameter.denominator.name,
      };
    }
    if (isStandard || isServing) {
      const numeratorCode = isPending && isServing ? existingSpecification?.numeratorUnitCode ?? '' 
      :existingSpecification?.numeratorUnitCode ?? parameter.numerator.code;
      const denominatorCode = isStandard ?
        existingSpecification?.denominatorUnitCode ?? parameter.denominator.code
        : this.appConfigService.configuration.servingPortionDenominatorCode;

      const numeratorUnit = units.filter(x => x.code === numeratorCode);
      specification.numeratorCode = (numeratorUnit.length === 0 ||
        existingSpecification?.numeratorUnitCode === this.appConfigService.configuration?.noUnitCode) ?
        null : numeratorUnit.shift();

      const denominatorUnit = units.filter(x => x.code === denominatorCode);
      specification.denominatorCode = (denominatorUnit.length === 0
        || existingSpecification?.denominatorUnitCode === this.appConfigService.configuration?.noUnitCode)
        ? null : denominatorUnit.shift();
    }
    return specification;
  }

  loadSpecificationGridHeaders(sample: Sample): ISpecificationHeader[] {
    const headers: ISpecificationHeader[] = [];
    headers.push({ field: TranslationService.translate('vbeiAnalyses'), header: TranslationService.translate('vbeiAnalyses'), visible: true});
    headers.push({ field: TranslationService.translate('vbeiParameters2'), header: TranslationService.translate('vbeiParameters2'), visible:true });
    headers.push({
      field: TranslationService.translate('vbeiStandardSpecification'),
      header: TranslationService.translate('vbeiStandardSpecification'), subCols: this.getSubColumns(true),
      visible: true
    });

    if (sample?.servingSize && Number(sample?.servingSize)) {
      headers.push({
        field: TranslationService.translate('vbeiServingSpecification'),
        header: TranslationService.translate('vbeiServingSpecification'), subCols: this.getSubColumns(true),
        visible: this.returnSpecificationVisibility(sample),
         });
    }
    if (sample?.resultConversion === ResultConversionCode.DV) {
      headers.push({
        field: TranslationService.translate('vbeiDailyValueSpecification'),
        header: TranslationService.translate('vbeiDailyValueSpecification'),
        subCols: this.getSubColumns(false),
        visible: true,
      });
    } else if (sample?.resultConversion === ResultConversionCode.LC) {
      headers.push({
        field: TranslationService.translate('vbeiLabelClaimSpecification'),
        header: TranslationService.translate('vbeiLabelClaimSpecification'), subCols: this.getSubColumns(false),
        visible: true
      });
    }

    return headers;
  }

  returnSpecificationVisibility(sample: Sample)
  {
    return sample.servingSize && sample.specifications.some(s =>
      s.parameters.some(p => p.servingSpecification.max || p.servingSpecification.min ||
         p.servingSpecification.target));
  }

  private getSubColumns(isStdOrServing: boolean) {
    let subColumns: ISubColumn[] = [];
    if (isStdOrServing) {
      subColumns.push({
        field: TranslationService.translate('vbeiUnits'),
        header: TranslationService.translate('vbeiUnits')
      });
    }
    subColumns = subColumns.concat([
      {
        field: TranslationService.translate('vbeiTarget'),
        header: TranslationService.translate('vbeiTarget')
      },
      {
        field: TranslationService.translate('vbeiMin'),
        header: TranslationService.translate('vbeiMin')
      },
      {
        field: TranslationService.translate('vbeiSpecificationMax'),
        header: TranslationService.translate('vbeiSpecificationMax')
      }
    ]);

    return subColumns;
  }

  getSpecificationsForSave(currentSample: Sample, isMultiple: boolean, specificationsViewModel: SpecificationViewModel[] = null){
    const specifications = (specificationsViewModel || currentSample.specifications)
    ? (specificationsViewModel || currentSample.specifications).map(spec => Object.assign(new SpecificationViewModel(), spec))
    : [];


    currentSample.specifications = ([] as SpecificationViewModel[]).concat(...specifications.map((p: SpecificationViewModel) => {
      p.parameters = p.parameters.filter(t => t.standardSpecification.hasValue || t.servingSpecification?.hasValue
        || t.dailyValueSpecification?.hasValue || t.labelClaimSpecification?.hasValue);
      return p;
    })).filter(t => t.parameters.length > 0);

    currentSample.isSpecificationsEdited = true;

    let specificationsForSave: SpecificationViewModel[] = JSON.parse(JSON.stringify(specificationsViewModel)).map((p: any) => p as SpecificationViewModel);
    specificationsForSave = ([] as SpecificationViewModel[]).concat(...specificationsForSave.map((p: SpecificationViewModel) => {
      p.parameters = p.parameters.filter(t => t.standardSpecification.isUpdated || t.servingSpecification?.isUpdated
        || t.dailyValueSpecification?.isUpdated || t.labelClaimSpecification?.isUpdated);
      return p;
    })).filter(t => t.parameters.length > 0);

    return specificationsForSave;
  }
  getSpecificationsForSaveMultiple(currentSample: Sample, isMultiple: boolean, selectedSample: Sample[], specificationsViewModel: SpecificationViewModel[]) {
    // --- Update: If isMultiple is true, ensure parameters in specificationViewModel and currentSample.specifications are the same for parameters with selected=false ---
    if (isMultiple && specificationsViewModel && currentSample.specifications) {
      specificationsViewModel.forEach(specVM => {
        // Find the matching spec in currentSample by testCode
        const currentSpec = currentSample.specifications.find(cs => cs.testCode === specVM.testCode);
        if (currentSpec) {
          specVM.parameters.forEach((param, idx) => {
            if (param.selected === false || param.selected === undefined) {
              // Find the parameter in currentSpec with the same parameterCode
              const currentParam = currentSpec.parameters.find(p => p.parameterCode === param.parameterCode);
              if (currentParam) {
                // Replace the parameter in specVM with the one from currentSpec
                specVM.parameters[idx] = currentParam;
              }
            }
          });
        }
      });
    }
    
    const specifications = (specificationsViewModel || currentSample.specifications)
      ? (specificationsViewModel || currentSample.specifications).map(spec => Object.assign(new SpecificationViewModel(), spec))
      : [];
    
    selectedSample.forEach(sample => {
      let existingSampleSpecification = sample.specifications
      .map(spec => Object.assign(new SpecificationViewModel(), spec))
      .filter(spec => spec.parameters.length > 0);
      let specificationWithValue = this.filterSpecifications(JSON.parse(JSON.stringify(specifications)), isMultiple);

      const existingParameterSpecifications = ([] as ParameterSpecification[])
      .concat(...sample.specifications.map((t: SpecificationViewModel) => t.parameters));

      existingParameterSpecifications.forEach(t => {
        specifications.forEach(s => {
          s.parameters.forEach(l => {
            if (t.parameterCode === l.parameterCode) {
              this.updateExistingSpecifications(t, l);
            }
          });
        });
      });

      // this.updateExistingSpecifications()
      existingSampleSpecification.forEach(ess=>{
        specificationWithValue.forEach(swv=>{
          if(ess.testCode === swv.testCode){
            ess.parameters.push(...swv.parameters.filter(param => 
              !ess.parameters.some(existingParam => existingParam.parameterCode === param.parameterCode)
            ));
          }
        })
      })
      const sampleConcatenatedSpecifications = existingSampleSpecification.concat(specificationWithValue.filter(spec => 
      !existingSampleSpecification.some(existingSpec => 
        existingSpec.testCode === spec.testCode
      )
      ));
      if(sampleConcatenatedSpecifications.length > 0){
        sample.specifications = this.filterSpecificationsBySampleParameters(sample, this.filterSpecifications(sampleConcatenatedSpecifications, false));
      }
      sample.isSpecificationsEdited = true;
    });

    let specificationsForSave: SpecificationViewModel[] = JSON.parse(JSON.stringify(specificationsViewModel)).map((p: any) => p as SpecificationViewModel);
    specificationsForSave =  ([] as SpecificationViewModel[]).concat(...specifications.map((p: SpecificationViewModel) => {
      p.parameters = p.parameters.filter(t => t.selected);
      return p;
      })).filter(t => t.parameters.length > 0);

    return specificationsForSave;
    }

    private filterSpecifications(specifications: SpecificationViewModel[], isMultiple: boolean = false): SpecificationViewModel[] {
      let specifcations = ([] as SpecificationViewModel[]).concat(...specifications.map((p: SpecificationViewModel) => {
      p.parameters = p.parameters.filter(t => 
        t.standardSpecification.hasValue || 
        t.servingSpecification?.hasValue ||
        t.dailyValueSpecification?.hasValue || 
        t.labelClaimSpecification?.hasValue
      );
      if(isMultiple){
        p.parameters = p.parameters.filter(t => t.selected);
      }
      return p;
      })).filter(t => t.parameters.length > 0);
      return specifcations;
    }

  /**
   * Filters specifications to only include parameters that exist in the sample's testParameters.
   * This ensures each sample only gets specifications for analyses/parameters it actually contains.
   */
  private filterSpecificationsBySampleParameters(sample: Sample, specifications: SpecificationViewModel[]): SpecificationViewModel[] {
    if (!sample?.testParameters || !specifications) {
      return specifications;
    }

    // Get all parameter codes that exist in this sample's testParameters
    const availableParameterCodes = new Set<string>();
    sample.testParameters.forEach(testParam => {
      testParam.parameters?.forEach(param => {
        if (param.code) {
          availableParameterCodes.add(param.code);
        }
      });
    });

    // Filter specifications to only include parameters that exist for this sample
    return specifications
      .map(spec => {
        const filteredParameters = spec.parameters.filter(param => 
          param.parameterCode && availableParameterCodes.has(param.parameterCode)
        );
        
        if (filteredParameters.length > 0) {
          return {
            ...spec,
            parameters: filteredParameters
          };
        }
        return null;
      })
      .filter(spec => spec !== null);
  }

  getMandatoryParametersWithMissingTarget(specificationsViewModel: SpecificationViewModel[]) {
    const specifications = JSON.parse(JSON.stringify(specificationsViewModel));
    const parameters = ([] as ParameterSpecification[]).concat(...specifications.map((t: SpecificationViewModel) => t.parameters));
    const mandatoryParameters = parameters.filter(t => t.required);
    const mandatoryParametersWithMissingTarget = mandatoryParameters
      .filter(t => !(t.standardSpecification.target));
    return mandatoryParametersWithMissingTarget;
  }
}
